import React, { useState, useEffect } from 'react';
import api from '../services/api';
import { useAuth } from '../context/AuthContext';
import { format, subDays, subMonths } from 'date-fns';
import {
  DocumentArrowDownIcon,
  CalendarIcon,
  ChartBarIcon,
  CurrencyDollarIcon,
  TruckIcon,
  UserGroupIcon,
  DocumentTextIcon,
  PrinterIcon,
  ArrowPathIcon,
  ExclamationTriangleIcon
} from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';
import DatePicker from 'react-datepicker';
import "react-datepicker/dist/react-datepicker.css";

const Reports = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [reportType, setReportType] = useState('sales');
  const [dateRange, setDateRange] = useState('today');
  const [customFromDate, setCustomFromDate] = useState(new Date(subDays(new Date(), 7)));
  const [customToDate, setCustomToDate] = useState(new Date());
  const [selectedShopOwner, setSelectedShopOwner] = useState('');
  const [selectedSalesAgent, setSelectedSalesAgent] = useState('');
  const [shopOwners, setShopOwners] = useState([]);
  const [salesAgents, setSalesAgents] = useState([]);
  const [previewData, setPreviewData] = useState(null);
  const [showPreview, setShowPreview] = useState(false);
  const [error, setError] = useState(null);
  const [financialStatementType, setFinancialStatementType] = useState('profit_loss');


  useEffect(() => {
    fetchFilters();
  }, []);

  const fetchFilters = async () => {
    try {
      const [ownersRes, agentsRes] = await Promise.all([
        api.get('/users/', { params: { user_type: 'shop_owner', per_page: 1000 } }),
        api.get('/users/', { params: { user_type: 'sales_agent', per_page: 1000 } })
      ]);
      
      console.log("ownersRes.data in Reports.js = ", ownersRes.data);
      console.log("agentsRes.data in Report.js = ", agentsRes.data);
      
      // Handle different response structures
      const owners = ownersRes.data.items || ownersRes.data || [];
      const agents = agentsRes.data.items || agentsRes.data || [];
      
      setShopOwners(owners);
      setSalesAgents(agents);
    } catch (error) {
      console.error('Failed to fetch filters:', error);
      toast.error('Failed to load filter options');
    }
  };

  const getDateParams = () => {
    const today = new Date();
    let fromDate, toDate;
    const weekStart = subDays(today, today.getDay());
    const lastMonth = subMonths(today, 1);
    
    switch (dateRange) {
      case 'today':
        fromDate = today;
        toDate = today;
        break;
      case 'yesterday':
        fromDate = subDays(today, 1);
        toDate = subDays(today, 1);
        break;
      case 'this_week':
        
        fromDate = weekStart;
        toDate = today;
        break;
      case 'this_month':
        fromDate = new Date(today.getFullYear(), today.getMonth(), 1);
        toDate = today;
        break;
      case 'last_month':
        
        fromDate = new Date(lastMonth.getFullYear(), lastMonth.getMonth(), 1);
        toDate = new Date(lastMonth.getFullYear(), lastMonth.getMonth() + 1, 0);
        break;
      case 'this_year':
        fromDate = new Date(today.getFullYear(), 0, 1);
        toDate = today;
        break;
      case 'custom':
        fromDate = customFromDate;
        toDate = customToDate;
        break;
      default:
        fromDate = today;
        toDate = today;
    }
    
    return {
      from_date: format(fromDate, 'yyyy-MM-dd'),
      to_date: format(toDate, 'yyyy-MM-dd')
    };
  };

  
  const handlePreview = async () => {
  setError(null);
  
  try {
    setLoading(true);
    
    // Validate customer selection
    if (reportType === 'customer' && !selectedShopOwner) {
      toast.error('Please select a customer');
      setLoading(false);
      return;
    }
    
    const dateParams = getDateParams();
    
    const params = {
      type: dateRange,
      from_date: dateParams.from_date,
      to_date: dateParams.to_date,
      format: 'json'
    };

    // Only add filters if they have values
    if (reportType === 'sales') {
      if (selectedShopOwner && selectedShopOwner !== 'undefined' && selectedShopOwner !== '') {
        params.shop_owner_id = selectedShopOwner;
      }
      if (selectedSalesAgent && selectedSalesAgent !== 'undefined' && selectedSalesAgent !== '') {
        params.sales_agent_id = selectedSalesAgent;
      }
    }

    let endpoint = '';
    switch (reportType) {
      case 'sales':
        endpoint = '/reports/sales';
        break;
      case 'inventory':
        endpoint = '/reports/inventory';
        break;
      case 'financial':
        endpoint = '/reports/financial-statements';
        params.statement_type = financialStatementType;
        break;
      case 'customer':
        if (!selectedShopOwner || selectedShopOwner === 'undefined' || selectedShopOwner === '') {
          toast.error('Please select a valid customer');
          setLoading(false);
          return;
        }
        endpoint = `/reports/customer-statement/${selectedShopOwner}`;
        break;
      default:
        toast.error('Invalid report type');
        setLoading(false);
        return;
    }

    console.log('Fetching report:', { endpoint, params });
    
    const response = await api.get(endpoint, { params });
    console.log('Report response:', response.data);
    
    setPreviewData(response.data);
    setShowPreview(true);
    toast.success('Report generated successfully');
  } catch (error) {
    console.error('Failed to generate preview:', error);
    let errorMessage = 'Failed to generate report';
    
    if (error.response) {
      errorMessage = error.response.data?.error || error.response.data?.message || `Server error: ${error.response.status}`;
      console.log('Error response:', error.response.data);
    } else if (error.request) {
      errorMessage = 'No response from server. Please check your connection.';
    } else {
      errorMessage = error.message;
    }
    
    setError(errorMessage);
    toast.error(errorMessage);
  } finally {
    setLoading(false);
  }
};

  const handleExport = async (format) => {
    setError(null);
    
    try {
      setLoading(true);
      
      // Validate customer selection
      if (reportType === 'customer' && !selectedShopOwner) {
        toast.error('Please select a customer');
        setLoading(false);
        return;
      }
      
      const dateParams = getDateParams();
      
      const params = {
        format,
        type: dateRange,
        from_date: dateParams.from_date,
        to_date: dateParams.to_date
      };

      if (reportType === 'sales') {
        if (selectedShopOwner) params.shop_owner_id = selectedShopOwner;
        if (selectedSalesAgent) params.sales_agent_id = selectedSalesAgent;
      }

      let endpoint = '';
      switch (reportType) {
        case 'sales':
          endpoint = '/reports/sales';
          break;
        case 'inventory':
          endpoint = '/reports/inventory';
          break;
        case 'financial':
          endpoint = '/reports/financial-statements';
          params.statement_type = financialStatementType;
          break;
        case 'customer':
          if (!selectedShopOwner || selectedShopOwner === 'undefined') {
            toast.error('Invalid customer selected');
            setLoading(false);
            return;
          }
          endpoint = `/reports/customer-statement/${selectedShopOwner}`;
          break;
        default:
          toast.error('Invalid report type');
          setLoading(false);
          return;
      }

      const response = await api.get(endpoint, {
        params,
        responseType: 'blob'
      });

      // Download file
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      
      const fileExtension = format === 'pdf' ? 'pdf' : format === 'excel' ? 'xlsx' : 'csv';
      const filename = `${reportType}_report_${format(new Date(), 'yyyyMMdd_HHmmss')}.${fileExtension}`;
      
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();

      toast.success('Report exported successfully');
    } catch (error) {
      console.error('Failed to export report:', error);
      const errorMessage = error.response?.data?.error || error.message || 'Failed to export report';
      setError(errorMessage);
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handlePrint = () => {
    window.print();
  };

  const reportTypes = [
    { 
      id: 'sales', 
      name: 'Sales Report', 
      icon: CurrencyDollarIcon, 
      roles: ['owner', 'manager', 'sales_agent', 'shop_owner'],
      description: 'View sales transactions, payments, and outstanding balances'
    },
    { 
      id: 'inventory', 
      name: 'Inventory Report', 
      icon: TruckIcon, 
      roles: ['owner', 'manager'],
      description: 'Current stock levels, values, and low stock alerts'
    },
    { 
      id: 'financial', 
      name: 'Financial Statements', 
      icon: ChartBarIcon, 
      roles: ['owner'],
      description: 'Profit & Loss, Balance Sheet, and Cash Flow statements'
    },
    { 
      id: 'customer', 
      name: 'Customer Statement', 
      icon: UserGroupIcon, 
      roles: ['owner', 'manager', 'sales_agent'],
      description: 'Detailed statement for individual customers'
    }
  ];

  const filteredReportTypes = reportTypes.filter(type => 
    type.roles.includes(user?.user_type)
  );

  const getShopOwnerId = (owner) => {
    return owner.shop_owner_id || owner.id;
  };

  const renderPreview = () => {
    if (!previewData) return null;

    // Sales Report Preview
    if (reportType === 'sales' && previewData.data) {
      return (
        <div className="mt-6">
          <h3 className="text-lg font-medium text-gray-900 mb-4">Sales Report Preview</h3>
          
          {/* Summary Cards */}
          <div className="grid grid-cols-1 gap-5 sm:grid-cols-4 mb-6">
            <div className="bg-white overflow-hidden shadow rounded-lg">
              <div className="px-4 py-5 sm:p-6">
                <dt className="text-sm font-medium text-gray-500 truncate">Total Sales</dt>
                <dd className="mt-1 text-2xl font-semibold text-gray-900">
                  ZMW {previewData.summary?.total_sales?.toLocaleString() || 0}
                </dd>
              </div>
            </div>
            <div className="bg-white overflow-hidden shadow rounded-lg">
              <div className="px-4 py-5 sm:p-6">
                <dt className="text-sm font-medium text-gray-500 truncate">Total Collected</dt>
                <dd className="mt-1 text-2xl font-semibold text-green-600">
                  ZMW {previewData.summary?.total_collected?.toLocaleString() || 0}
                </dd>
              </div>
            </div>
            <div className="bg-white overflow-hidden shadow rounded-lg">
              <div className="px-4 py-5 sm:p-6">
                <dt className="text-sm font-medium text-gray-500 truncate">Outstanding</dt>
                <dd className="mt-1 text-2xl font-semibold text-red-600">
                  ZMW {previewData.summary?.outstanding?.toLocaleString() || 0}
                </dd>
              </div>
            </div>
            <div className="bg-white overflow-hidden shadow rounded-lg">
              <div className="px-4 py-5 sm:p-6">
                <dt className="text-sm font-medium text-gray-500 truncate">Transactions</dt>
                <dd className="mt-1 text-2xl font-semibold text-gray-900">
                  {previewData.summary?.transaction_count || 0}
                </dd>
              </div>
            </div>
          </div>

          {/* Preview Table */}
          <div className="bg-white shadow overflow-hidden sm:rounded-lg">
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Transaction #</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Customer</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Type</th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Amount</th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Paid</th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Balance</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {previewData.data?.slice(0, 10).map((row, index) => (
                    <tr key={index}>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{row.Date}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{row['Transaction #']}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{row.Shop}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{row.Type}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 text-right">
                        ZMW {row.Amount?.toLocaleString() || 0}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 text-right">
                        ZMW {row.Paid?.toLocaleString() || 0}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 text-right">
                        ZMW {row.Balance?.toLocaleString() || 0}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {previewData.data?.length > 10 && (
                <div className="px-6 py-4 bg-gray-50 text-sm text-gray-500 text-center">
                  Showing 10 of {previewData.data.length} transactions
                </div>
              )}
            </div>
          </div>
        </div>
      );
    }

    // Inventory Report Preview
    if (reportType === 'inventory' && previewData.data) {
      return (
        <div className="mt-6">
          <h3 className="text-lg font-medium text-gray-900 mb-4">Inventory Report Preview</h3>
          
          {/* Summary Cards */}
          <div className="grid grid-cols-1 gap-5 sm:grid-cols-3 mb-6">
            <div className="bg-white overflow-hidden shadow rounded-lg">
              <div className="px-4 py-5 sm:p-6">
                <dt className="text-sm font-medium text-gray-500 truncate">Total Products</dt>
                <dd className="mt-1 text-2xl font-semibold text-gray-900">
                  {previewData.summary?.total_products || 0}
                </dd>
              </div>
            </div>
            <div className="bg-white overflow-hidden shadow rounded-lg">
              <div className="px-4 py-5 sm:p-6">
                <dt className="text-sm font-medium text-gray-500 truncate">Total Cases</dt>
                <dd className="mt-1 text-2xl font-semibold text-gray-900">
                  {previewData.summary?.total_cases || 0}
                </dd>
              </div>
            </div>
            <div className="bg-white overflow-hidden shadow rounded-lg">
              <div className="px-4 py-5 sm:p-6">
                <dt className="text-sm font-medium text-gray-500 truncate">Total Value</dt>
                <dd className="mt-1 text-2xl font-semibold text-gray-900">
                  ZMW {previewData.summary?.total_inventory_value?.toLocaleString() || 0}
                </dd>
              </div>
            </div>
          </div>

          {/* Inventory Table */}
          <div className="bg-white shadow overflow-hidden sm:rounded-lg">
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Product</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Brand</th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Initial Cases in Depot</th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Current Cases in Depot</th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Avg Cost</th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Inventory Value</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {previewData.data?.slice(0, 10).map((item, index) => (
                    <tr key={index}>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{item.Product}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{item.Brand}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 text-right">{item['Cases in Depot']}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 text-right">{item['Total Cases (All)']}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 text-right">ZMW {item['Avg Cost']?.toLocaleString()}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 text-right">ZMW {item['Inventory Value']?.toLocaleString()}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      );
    }

    // Financial Statements Preview
    if (reportType === 'financial' && previewData) {
      // Check which type of statement we're displaying
      const isProfitLoss = previewData.revenue !== undefined;
      const isBalanceSheet = previewData.assets !== undefined;
      const isCashFlow = previewData.operating_activities !== undefined;
      
      if (isProfitLoss) {
        // Profit & Loss display
        return (
          <div className="mt-6">
            <h3 className="text-lg font-medium text-gray-900 mb-4">Profit & Loss Statement</h3>
            
            {/* Period Info */}
            <div className="bg-gray-50 p-4 rounded-lg mb-6">
              <p className="text-sm text-gray-600">
                Period: {previewData.period?.from} to {previewData.period?.to}
              </p>
            </div>

            {/* Revenue */}
            <div className="bg-white shadow overflow-hidden sm:rounded-lg mb-4">
              <div className="px-4 py-5 sm:p-6">
                <div className="flex justify-between items-center">
                  <h4 className="text-lg font-medium text-gray-900">Revenue</h4>
                  <span className="text-xl font-bold text-gray-900">
                    ZMW {previewData.revenue?.total_sales?.toLocaleString() || 0}
                  </span>
                </div>
              </div>
            </div>

            {/* Cost of Goods Sold */}
            <div className="bg-white shadow overflow-hidden sm:rounded-lg mb-4">
              <div className="px-4 py-5 sm:p-6">
                <div className="flex justify-between items-center">
                  <h4 className="text-lg font-medium text-gray-900">Cost of Goods Sold</h4>
                  <span className="text-xl font-bold text-red-600">
                    (ZMW {previewData.cost_of_goods_sold?.toLocaleString() || 0})
                  </span>
                </div>
              </div>
            </div>

            {/* Gross Profit */}
            <div className="bg-green-50 shadow overflow-hidden sm:rounded-lg mb-4">
              <div className="px-4 py-5 sm:p-6">
                <div className="flex justify-between items-center">
                  <h4 className="text-lg font-medium text-gray-900">Gross Profit</h4>
                  <span className="text-xl font-bold text-green-600">
                    ZMW {previewData.gross_profit?.toLocaleString() || 0}
                  </span>
                </div>
                <p className="text-sm text-gray-600 mt-1">Margin: {previewData.gross_margin?.toFixed(2)}%</p>
              </div>
            </div>

            {/* Expenses */}
            <div className="bg-white shadow overflow-hidden sm:rounded-lg mb-4">
              <div className="px-4 py-5 sm:p-6">
                <h4 className="text-lg font-medium text-gray-900 mb-4">Expenses</h4>
                
                {previewData.expenses?.salaries > 0 && (
                  <div className="flex justify-between py-2">
                    <span className="text-gray-600">Salaries</span>
                    <span className="font-medium">ZMW {previewData.expenses.salaries?.toLocaleString() || 0}</span>
                  </div>
                )}
                
                {previewData.expenses?.other_expenses?.map((expense, index) => (
                  <div key={index} className="flex justify-between py-2">
                    <span className="text-gray-600">{expense.category}</span>
                    <span className="font-medium">ZMW {expense.amount?.toLocaleString() || 0}</span>
                  </div>
                ))}
                
                <div className="flex justify-between py-2 border-t border-gray-200 mt-2 pt-2">
                  <span className="font-medium text-gray-900">Total Expenses</span>
                  <span className="font-bold text-red-600">ZMW {previewData.expenses?.total_expenses?.toLocaleString() || 0}</span>
                </div>
              </div>
            </div>

            {/* Net Profit */}
            <div className={`${previewData.net_profit >= 0 ? 'bg-green-50' : 'bg-red-50'} shadow overflow-hidden sm:rounded-lg`}>
              <div className="px-4 py-5 sm:p-6">
                <div className="flex justify-between items-center">
                  <h4 className="text-lg font-medium text-gray-900">Net Profit</h4>
                  <span className={`text-xl font-bold ${previewData.net_profit >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                    ZMW {previewData.net_profit?.toLocaleString() || 0}
                  </span>
                </div>
                <p className="text-sm text-gray-600 mt-1">Margin: {previewData.net_margin?.toFixed(2)}%</p>
              </div>
            </div>
          </div>
        );
      } else if (isBalanceSheet) {
        // Balance Sheet display
        return (
          <div className="mt-6">
            <h3 className="text-lg font-medium text-gray-900 mb-4">Balance Sheet</h3>
            
            {/* As of Date */}
            <div className="bg-gray-50 p-4 rounded-lg mb-6">
              <p className="text-sm text-gray-600">As of: {previewData.as_of}</p>
            </div>

            {/* Assets */}
            <div className="bg-white shadow overflow-hidden sm:rounded-lg mb-4">
              <div className="px-4 py-5 sm:p-6">
                <h4 className="text-lg font-medium text-gray-900 mb-4">Assets</h4>
                
                <div className="space-y-2">
                  <div className="flex justify-between py-2">
                    <span className="text-gray-600">Cash</span>
                    <span className="font-medium">ZMW {previewData.assets?.current_assets?.cash?.toLocaleString() || 0}</span>
                  </div>
                  <div className="flex justify-between py-2">
                    <span className="text-gray-600">Accounts Receivable</span>
                    <span className="font-medium">ZMW {previewData.assets?.current_assets?.accounts_receivable?.toLocaleString() || 0}</span>
                  </div>
                  <div className="flex justify-between py-2">
                    <span className="text-gray-600">Inventory</span>
                    <span className="font-medium">ZMW {previewData.assets?.current_assets?.inventory?.toLocaleString() || 0}</span>
                  </div>
                  
                  <div className="flex justify-between py-2 border-t border-gray-200 mt-2 pt-2">
                    <span className="font-medium text-gray-900">Total Current Assets</span>
                    <span className="font-bold text-gray-900">ZMW {previewData.assets?.total_current_assets?.toLocaleString() || 0}</span>
                  </div>
                  
                  <div className="flex justify-between py-2 border-t-2 border-gray-300 mt-2 pt-2">
                    <span className="font-bold text-gray-900">Total Assets</span>
                    <span className="font-bold text-gray-900">ZMW {previewData.assets?.total_assets?.toLocaleString() || 0}</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Liabilities & Equity */}
            <div className="bg-white shadow overflow-hidden sm:rounded-lg">
              <div className="px-4 py-5 sm:p-6">
                <h4 className="text-lg font-medium text-gray-900 mb-4">Liabilities & Equity</h4>
                
                <h5 className="font-medium text-gray-700 mt-4 mb-2">Liabilities</h5>
                <div className="space-y-2">
                  <div className="flex justify-between py-2">
                    <span className="text-gray-600">Accounts Payable</span>
                    <span className="font-medium">ZMW {previewData.liabilities?.current_liabilities?.accounts_payable?.toLocaleString() || 0}</span>
                  </div>
                  <div className="flex justify-between py-2 border-t border-gray-200">
                    <span className="font-medium text-gray-900">Total Liabilities</span>
                    <span className="font-bold text-gray-900">ZMW {previewData.liabilities?.total_liabilities?.toLocaleString() || 0}</span>
                  </div>
                </div>
                
                <h5 className="font-medium text-gray-700 mt-6 mb-2">Equity</h5>
                <div className="space-y-2">
                  <div className="flex justify-between py-2">
                    <span className="text-gray-600">Capital</span>
                    <span className="font-medium">ZMW {previewData.equity?.capital?.toLocaleString() || 0}</span>
                  </div>
                  <div className="flex justify-between py-2">
                    <span className="text-gray-600">Retained Earnings</span>
                    <span className="font-medium">ZMW {previewData.equity?.retained_earnings?.toLocaleString() || 0}</span>
                  </div>
                  <div className="flex justify-between py-2">
                    <span className="text-gray-600">Current Period Profit</span>
                    <span className="font-medium">ZMW {previewData.equity?.current_period_profit?.toLocaleString() || 0}</span>
                  </div>
                  <div className="flex justify-between py-2 border-t border-gray-200 mt-2 pt-2">
                    <span className="font-medium text-gray-900">Total Equity</span>
                    <span className="font-bold text-gray-900">ZMW {previewData.equity?.total_equity?.toLocaleString() || 0}</span>
                  </div>
                </div>
                
                <div className="flex justify-between py-2 border-t-2 border-gray-300 mt-4 pt-4">
                  <span className="font-bold text-gray-900">Total Liabilities & Equity</span>
                  <span className="font-bold text-gray-900">
                    ZMW {((previewData.liabilities?.total_liabilities || 0) + (previewData.equity?.total_equity || 0)).toLocaleString()}
                  </span>
                </div>
              </div>
            </div>
          </div>
        );
      } else if (isCashFlow) {
        // Cash Flow Statement display
        return (
          <div className="mt-6">
            <h3 className="text-lg font-medium text-gray-900 mb-4">Cash Flow Statement</h3>
            
            {/* Period Info */}
            <div className="bg-gray-50 p-4 rounded-lg mb-6">
              <p className="text-sm text-gray-600">
                Period: {previewData.period?.from} to {previewData.period?.to}
              </p>
            </div>

            {/* Operating Activities */}
            <div className="bg-white shadow overflow-hidden sm:rounded-lg mb-4">
              <div className="px-4 py-5 sm:p-6">
                <h4 className="text-lg font-medium text-gray-900 mb-4">Operating Activities</h4>
                
                <div className="space-y-2">
                  <div className="flex justify-between py-2">
                    <span className="text-gray-600">Cash Received from Customers</span>
                    <span className="font-medium">ZMW {previewData.operating_activities?.cash_received_from_customers?.toLocaleString() || 0}</span>
                  </div>
                  <div className="flex justify-between py-2">
                    <span className="text-gray-600">Cash Paid for Expenses</span>
                    <span className="font-medium text-red-600">(ZMW {previewData.operating_activities?.cash_paid_for_expenses?.toLocaleString() || 0})</span>
                  </div>
                  <div className="flex justify-between py-2 border-t border-gray-200 mt-2 pt-2">
                    <span className="font-medium text-gray-900">Net Cash from Operating Activities</span>
                    <span className="font-bold text-gray-900">ZMW {previewData.operating_activities?.net_cash_from_operating?.toLocaleString() || 0}</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Investing Activities */}
            <div className="bg-white shadow overflow-hidden sm:rounded-lg mb-4">
              <div className="px-4 py-5 sm:p-6">
                <h4 className="text-lg font-medium text-gray-900 mb-4">Investing Activities</h4>
                <div className="flex justify-between py-2">
                  <span className="text-gray-600">Net Cash from Investing</span>
                  <span className="font-medium">ZMW {previewData.investing_activities?.net_cash_from_investing?.toLocaleString() || 0}</span>
                </div>
              </div>
            </div>

            {/* Financing Activities */}
            <div className="bg-white shadow overflow-hidden sm:rounded-lg mb-4">
              <div className="px-4 py-5 sm:p-6">
                <h4 className="text-lg font-medium text-gray-900 mb-4">Financing Activities</h4>
                <div className="flex justify-between py-2">
                  <span className="text-gray-600">Net Cash from Financing</span>
                  <span className="font-medium">ZMW {previewData.financing_activities?.net_cash_from_financing?.toLocaleString() || 0}</span>
                </div>
              </div>
            </div>

            {/* Net Change in Cash */}
            <div className="bg-blue-50 shadow overflow-hidden sm:rounded-lg mb-4">
              <div className="px-4 py-5 sm:p-6">
                <div className="flex justify-between items-center">
                  <h4 className="text-lg font-medium text-gray-900">Net Increase in Cash</h4>
                  <span className="text-xl font-bold text-blue-600">
                    ZMW {previewData.net_increase_in_cash?.toLocaleString() || 0}
                  </span>
                </div>
              </div>
            </div>

            {/* Cash Balances */}
            <div className="grid grid-cols-2 gap-4">
              <div className="bg-gray-50 p-4 rounded-lg">
                <p className="text-sm text-gray-600">Cash at Beginning</p>
                <p className="text-2xl font-bold text-gray-700">ZMW {previewData.cash_at_beginning?.toLocaleString() || 0}</p>
              </div>
              <div className="bg-green-50 p-4 rounded-lg">
                <p className="text-sm text-green-600">Cash at End</p>
                <p className="text-2xl font-bold text-green-700">ZMW {previewData.cash_at_end?.toLocaleString() || 0}</p>
              </div>
            </div>
          </div>
        );
      }
    }

    // Customer Statement Preview - ADD THIS SECTION
    if (reportType === 'customer' && previewData) {
      return (
        <div className="mt-6">
          <h3 className="text-lg font-medium text-gray-900 mb-4">Customer Statement</h3>
          
          {/* Customer Info */}
          <div className="bg-gray-50 p-4 rounded-lg mb-6 grid grid-cols-2 gap-4">
            <div>
              <p className="text-xs text-gray-500">Business Name</p>
              <p className="font-medium">{previewData.shop_owner?.business_name || '-'}</p>
            </div>
            <div>
              <p className="text-xs text-gray-500">Contact Person</p>
              <p className="font-medium">{previewData.shop_owner?.user?.full_name || '-'}</p>
            </div>
            <div>
              <p className="text-xs text-gray-500">Location</p>
              <p className="font-medium">{previewData.shop_owner?.town || '-'}</p>
            </div>
            <div>
              <p className="text-xs text-gray-500">Phone</p>
              <p className="font-medium">{previewData.shop_owner?.business_phone || previewData.shop_owner?.user?.phone_number || '-'}</p>
            </div>
          </div>

          {/* Period Info */}
          <div className="bg-white p-4 rounded-lg border border-gray-200 mb-6">
            <p className="text-sm text-gray-600">
              Period: {previewData.period?.from} to {previewData.period?.to}
            </p>
          </div>

          {/* Balance Summary */}
          <div className="grid grid-cols-2 gap-4 mb-6">
            <div className="bg-green-50 p-4 rounded-lg">
              <p className="text-sm text-green-600">Opening Balance</p>
              <p className="text-2xl font-bold text-green-700">
                ZMW {previewData.opening_balance?.toLocaleString() || 0}
              </p>
            </div>
            <div className={`${previewData.closing_balance > 0 ? 'bg-red-50' : 'bg-green-50'} p-4 rounded-lg`}>
              <p className={`text-sm ${previewData.closing_balance > 0 ? 'text-red-600' : 'text-green-600'}`}>
                Closing Balance
              </p>
              <p className={`text-2xl font-bold ${previewData.closing_balance > 0 ? 'text-red-700' : 'text-green-700'}`}>
                ZMW {previewData.closing_balance?.toLocaleString() || 0}
              </p>
            </div>
          </div>

          {/* Credit Info */}
          {previewData.shop_owner?.credit_limit > 0 && (
            <div className="bg-white p-4 rounded-lg border border-gray-200 mb-6">
              <div className="flex justify-between items-center">
                <div>
                  <p className="text-sm text-gray-600">Credit Limit</p>
                  <p className="text-lg font-semibold text-gray-900">
                    ZMW {previewData.shop_owner.credit_limit?.toLocaleString()}
                  </p>
                </div>
                <div className="text-right">
                  <p className="text-sm text-gray-600">Available Credit</p>
                  <p className="text-lg font-semibold text-green-600">
                    ZMW {(previewData.shop_owner.credit_limit - previewData.closing_balance).toLocaleString()}
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Transactions Table */}
          <div className="bg-white shadow overflow-hidden sm:rounded-lg">
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Description</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Reference</th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Debit (ZMW)</th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Credit (ZMW)</th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Balance (ZMW)</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {previewData.entries?.map((entry, index) => (
                    <tr key={index} className={index === 0 ? 'bg-gray-50 font-medium' : ''}>
                      <td className="px-4 py-3 text-sm text-gray-900">{entry.date}</td>
                      <td className="px-4 py-3 text-sm text-gray-900">{entry.description}</td>
                      <td className="px-4 py-3 text-sm text-gray-500">{entry.reference}</td>
                      <td className="px-4 py-3 text-sm text-gray-900 text-right">
                        {entry.debit > 0 ? entry.debit.toLocaleString() : '-'}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-900 text-right">
                        {entry.credit > 0 ? entry.credit.toLocaleString() : '-'}
                      </td>
                      <td className="px-4 py-3 text-sm font-medium text-right">
                        <span className={entry.balance > 0 ? 'text-red-600' : 'text-green-600'}>
                          {entry.balance.toLocaleString()}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              
              {/* No entries message */}
              {(!previewData.entries || previewData.entries.length === 1) && (
                <div className="text-center py-8 text-gray-500">
                  No transactions found for this period.
                </div>
              )}
            </div>
          </div>

          {/* Footer */}
          <div className="mt-4 text-center text-sm text-gray-500">
            <p>This is a computer generated statement. No signature required.</p>
          </div>
        </div>
      );
    }

    return null;
  };

  return (
    <div className={showPreview ? 'print:p-0' : ''}>
      <div className="sm:flex sm:items-center sm:justify-between print:hidden">
        <div className="sm:flex-auto">
          <h1 className="text-2xl font-semibold text-gray-900">Reports</h1>
          <p className="mt-2 text-sm text-gray-700">
            Generate and export various business reports.
          </p>
        </div>
      </div>

      <div className="mt-8 grid grid-cols-1 gap-6 lg:grid-cols-4 print:hidden">
        {/* Report Type Selection */}
        <div className="lg:col-span-1">
          <div className="bg-white shadow rounded-lg p-6">
            <h2 className="text-lg font-medium text-gray-900 mb-4">Report Type</h2>
            <nav className="space-y-2">
              {filteredReportTypes.map((type) => (
                <button
                  key={type.id}
                  onClick={() => {
                    setReportType(type.id);
                    setShowPreview(false);
                    setPreviewData(null);
                    setError(null);
                  }}
                  className={`w-full flex items-start px-3 py-3 text-sm font-medium rounded-md ${
                    reportType === type.id
                      ? 'bg-primary-50 text-primary-700'
                      : 'text-gray-600 hover:bg-gray-50'
                  }`}
                >
                  <type.icon className={`mr-3 h-5 w-5 mt-0.5 ${
                    reportType === type.id ? 'text-primary-500' : 'text-gray-400'
                  }`} />
                  <div className="text-left">
                    <div>{type.name}</div>
                    <div className={`text-xs mt-1 ${
                      reportType === type.id ? 'text-primary-500' : 'text-gray-400'
                    }`}>
                      {type.description}
                    </div>
                  </div>
                </button>
              ))}
            </nav>
          </div>
        </div>

        {/* Report Options */}
        <div className="lg:col-span-3">
          <div className="bg-white shadow rounded-lg p-6">
            <h2 className="text-lg font-medium text-gray-900 mb-4">Report Options</h2>
            
            {error && (
              <div className="mb-4 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded relative" role="alert">
                <div className="flex">
                  <ExclamationTriangleIcon className="h-5 w-5 mr-2" />
                  <span className="text-sm">{error}</span>
                </div>
              </div>
            )}
            
            <div className="space-y-6">
              {/* Date Range */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  <CalendarIcon className="h-4 w-4 inline mr-1" />
                  Date Range
                </label>
                <select
                  className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                  value={dateRange}
                  onChange={(e) => setDateRange(e.target.value)}
                >
                  <option value="today">Today</option>
                  <option value="yesterday">Yesterday</option>
                  <option value="this_week">This Week</option>
                  <option value="this_month">This Month</option>
                  <option value="last_month">Last Month</option>
                  <option value="this_year">This Year</option>
                  <option value="custom">Custom Range</option>
                </select>
              </div>

              {dateRange === 'custom' && (
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700">From Date</label>
                    <DatePicker
                      selected={customFromDate}
                      onChange={date => setCustomFromDate(date)}
                      selectsStart
                      startDate={customFromDate}
                      endDate={customToDate}
                      maxDate={customToDate}
                      className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                      dateFormat="yyyy-MM-dd"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">To Date</label>
                    <DatePicker
                      selected={customToDate}
                      onChange={date => setCustomToDate(date)}
                      selectsEnd
                      startDate={customFromDate}
                      endDate={customToDate}
                      minDate={customFromDate}
                      maxDate={new Date()}
                      className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                      dateFormat="yyyy-MM-dd"
                    />
                  </div>
                </div>
              )}

              {/* Report-specific filters */}
              {reportType === 'sales' && (
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Shop Owner (Optional)</label>
                    <select
                      className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                      value={selectedShopOwner}
                      onChange={(e) => setSelectedShopOwner(e.target.value)}
                    >
                      <option value="">All Shop Owners</option>
                      {shopOwners.map(owner => (
                        <option key={owner.id} value={getShopOwnerId(owner)}>
                          {owner.profile?.business_name || owner.business_name || owner.full_name}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Sales Agent (Optional)</label>
                    <select
                      className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                      value={selectedSalesAgent}
                      onChange={(e) => setSelectedSalesAgent(e.target.value)}
                    >
                      <option value="">All Sales Agents</option>
                      {salesAgents.map(agent => (
                        <option key={agent.id} value={agent.profile?.id || agent.id}>
                          {agent.full_name}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              )}

              {reportType === 'customer' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700">Select Customer *</label>
                  <select
                    required
                    className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                    value={selectedShopOwner}
                    onChange={(e) => setSelectedShopOwner(e.target.value)}
                  >
                    <option value="">Choose a customer</option>
                    {shopOwners.map(owner => (
                      <option key={owner.id} value={getShopOwnerId(owner)}>
                        {owner.profile?.business_name || owner.business_name || owner.full_name} - {owner.town || owner.profile?.town || ''}
                      </option>
                    ))}
                  </select>
                </div>
              )}

              {reportType === 'financial' && (
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Statement Type
                  </label>
                  <select
                    className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                    value={financialStatementType}
                    onChange={(e) => setFinancialStatementType(e.target.value)}
                  >
                    <option value="profit_loss">Profit & Loss Statement</option>
                    <option value="balance_sheet">Balance Sheet</option>
                    <option value="cash_flow">Cash Flow Statement</option>
                  </select>
                </div>
              )}

              {/* Action Buttons */}
              <div className="flex space-x-3 pt-4">
                <button
                  onClick={handlePreview}
                  disabled={loading}
                  className="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50"
                >
                  <ArrowPathIcon className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
                  Preview
                </button>
                
                <div className="flex-1"></div>

                <button
                  onClick={() => handleExport('pdf')}
                  disabled={loading}
                  className="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
                >
                  <DocumentTextIcon className="h-4 w-4 mr-2" />
                  PDF
                </button>
                <button
                  onClick={() => handleExport('excel')}
                  disabled={loading}
                  className="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
                >
                  <DocumentArrowDownIcon className="h-4 w-4 mr-2" />
                  Excel
                </button>
                <button
                  onClick={() => handleExport('csv')}
                  disabled={loading}
                  className="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
                >
                  <DocumentTextIcon className="h-4 w-4 mr-2" />
                  CSV
                </button>
                <button
                  onClick={handlePrint}
                  disabled={loading}
                  className="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
                >
                  <PrinterIcon className="h-4 w-4 mr-2" />
                  Print
                </button>
              </div>

              {loading && (
                <div className="text-center py-4">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600 mx-auto"></div>
                  <p className="mt-2 text-sm text-gray-500">Generating report...</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Report Preview */}
      {showPreview && previewData && (
        <div className={`mt-8 ${showPreview ? 'print:mt-0' : ''}`}>
          <div className="bg-white shadow rounded-lg p-6">
            <div className="flex justify-between items-center mb-4 print:hidden">
              <h2 className="text-lg font-medium text-gray-900">Report Preview</h2>
              <button
                onClick={() => setShowPreview(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                ×
              </button>
            </div>
            {renderPreview()}
          </div>
        </div>
      )}
    </div>
  );
};

export default Reports;